import { getCanonicalUrl } from './seo';

export const APP_NAME = 'Startup Poker';
export const ORGANIZATION_NAME = 'Kromatic';
export const TOOL_SLUG = 'startup-poker';
export const TEMPLATE_VALUE_PATTERN = /\{[^}]+\}/;

const APPROVED_LESSON_HEADER_KEYS = ['1.1', '2', '3', '4', '5', '6'];

export type SeoManifestEntry = {
    description: string;
    include_in_llms: boolean;
    include_in_sitemap: boolean;
    lastmod: string;
    page_type: 'landing' | 'lesson_header';
    title: string;
    url: string;
};

export type SeoManifestDocument = {
    entries: SeoManifestEntry[];
    generated_at: string;
    tool_slug: string;
};

function collapseWhitespace(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
}

function stripHtml(value: string): string {
    return value.replace(/<[^>]+>/g, ' ');
}

function getLessonText(lesson: IntroContent | WizardContent | undefined): string {
    if (typeof lesson?.text !== 'string') {
        return '';
    }

    return collapseWhitespace(stripHtml(lesson.text));
}

function getStableLessonTitle(lesson: IntroContent | WizardContent | undefined): string {
    if (typeof lesson?.title !== 'string' || TEMPLATE_VALUE_PATTERN.test(lesson.title)) {
        return '';
    }

    return collapseWhitespace(stripHtml(lesson.title));
}

export function getLessonKeyFromRoutePath(routePath: string): string | null {
    return routePath.match(/^\/lessons\/([^/?#]+)/)?.[1] || null;
}

export function isApprovedLessonHeaderKey(lessonKey: string | null | undefined): boolean {
    return !!lessonKey && APPROVED_LESSON_HEADER_KEYS.includes(lessonKey);
}

export function getApprovedLessonHeaderKeys(): string[] {
    return [...APPROVED_LESSON_HEADER_KEYS];
}

export function getLessonSeoTitle(routePath: string, lessons: LessonsSetup, fallbackTitle: string): string {
    const lessonKey = getLessonKeyFromRoutePath(routePath);
    if (!lessonKey) {
        return fallbackTitle;
    }

    const lessonTitle = getStableLessonTitle(lessons?.[lessonKey]);
    return lessonTitle ? `${lessonTitle} | ${APP_NAME} | ${ORGANIZATION_NAME}` : fallbackTitle;
}

export function getLessonSeoDescription(routePath: string, lessons: LessonsSetup, fallbackDescription: string): string {
    const lessonKey = getLessonKeyFromRoutePath(routePath);
    if (!isApprovedLessonHeaderKey(lessonKey)) {
        return fallbackDescription;
    }

    const lessonText = getLessonText(lessons?.[lessonKey || '']);
    return lessonText || fallbackDescription;
}

export function getLessonFaqStructuredData(routePath: string, lessons: LessonsSetup, runtimeHostname?: string) {
    const lessonKey = getLessonKeyFromRoutePath(routePath);
    if (!isApprovedLessonHeaderKey(lessonKey)) {
        return null;
    }

    const lesson = lessons?.[lessonKey || ''];
    const lessonTitle = getStableLessonTitle(lesson) || `${APP_NAME} lesson`;
    const lessonSummary =
        getLessonText(lesson) ||
        'This lesson introduces a gated Startup Poker scenario that opens after signup or login.';
    const canonicalUrl = getCanonicalUrl(routePath, runtimeHostname);

    return {
        '@id': `${canonicalUrl}#faq`,
        '@type': 'FAQPage',
        mainEntity: [
            {
                '@type': 'Question',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: `${lessonTitle} is a Startup Poker lesson page on Kromatic. The public lesson URL stays readable for search and AI discovery, and the interactive simulation opens after signup or login.`,
                },
                name: `What is ${lessonTitle}?`,
            },
            {
                '@type': 'Question',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: lessonSummary,
                },
                name: `What will I learn in ${lessonTitle}?`,
            },
            {
                '@type': 'Question',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: `Yes. ${lessonTitle} keeps its public URL, but the guided gameplay experience starts after you create an account or sign in.`,
                },
                name: `Do I need an account to play ${lessonTitle}?`,
            },
        ],
        url: canonicalUrl,
    };
}

function getManifestTimestamp(): string {
    const configuredTimestamp =
        process.env.NEXT_PUBLIC_BUILD_TIMESTAMP ||
        process.env.BUILD_TIMESTAMP ||
        '';

    if (configuredTimestamp) {
        const parsedTimestamp = new Date(configuredTimestamp);
        if (!Number.isNaN(parsedTimestamp.getTime())) {
            return parsedTimestamp.toISOString();
        }
    }

    return new Date().toISOString();
}

function getLandingManifestEntry(runtimeHostname?: string): SeoManifestEntry {
    return {
        description:
            'Master decision-making in uncertainty with Startup Poker. Practice go/no-go decisions, information-value tradeoffs, and competition strategy.',
        include_in_llms: true,
        include_in_sitemap: true,
        lastmod: getManifestTimestamp(),
        page_type: 'landing',
        title: `${APP_NAME} | ${ORGANIZATION_NAME}`,
        url: getCanonicalUrl('/', runtimeHostname),
    };
}

function getLessonManifestEntries(lessons: LessonsSetup, runtimeHostname?: string): SeoManifestEntry[] {
    const entries: Array<SeoManifestEntry | null> = getApprovedLessonHeaderKeys()
        .map(lessonKey => {
            const lesson = lessons?.[lessonKey];
            const lessonTitle = getStableLessonTitle(lesson);
            if (!lessonTitle) {
                return null;
            }

            return {
                description:
                    getLessonText(lesson) ||
                    'This Startup Poker lesson introduces a gated simulation that continues after signup or login.',
                include_in_llms: true,
                include_in_sitemap: true,
                lastmod: getManifestTimestamp(),
                page_type: 'lesson_header' as const,
                title: `${lessonTitle} | ${APP_NAME} | ${ORGANIZATION_NAME}`,
                url: getCanonicalUrl(`/lessons/${lessonKey}`, runtimeHostname),
            };
        });

    return entries.filter((entry): entry is SeoManifestEntry => entry !== null);
}

export function getToolSeoManifest(lessons: LessonsSetup, runtimeHostname?: string): SeoManifestDocument {
    const generatedAt = getManifestTimestamp();
    const entries = [
        {
            ...getLandingManifestEntry(runtimeHostname),
            lastmod: generatedAt,
        },
        ...getLessonManifestEntries(lessons, runtimeHostname).map(entry => ({
            ...entry,
            lastmod: generatedAt,
        })),
    ];

    return {
        entries,
        generated_at: generatedAt,
        tool_slug: TOOL_SLUG,
    };
}
